import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	Statistic,
	Checkbox,
	Select,
	Tag,
	Row,
	Col,
	Input,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import {
	EditFilled,
	PlusCircleFilled,
	RollbackOutlined,
} from "@ant-design/icons";
import { franchises57Options } from "../../constants/Customer";
const { Text } = Typography;

const CogList = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [cogList, setCogList] = useState([]);
	const [franchise, setFranchise] = useState("57Concrete");
	const [search, setSearch] = useState("");

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "",
			dataIndex: "edit",
			width: 100,
			render: (_, record) => {
				return (
					<Button
						type="primary"
						size="large"
						icon={<EditFilled twoToneColor={"white"} />}
						onClick={(e) => history.push(`/cog/${record.id}`)}
					/>
				);
			},
		},
		{
			title: "Material Name",
			dataIndex: "material",
			key: "material",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Code",
			dataIndex: "code",
			key: "code",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Unit Price",
			dataIndex: "unitPrice",
			key: "unitPrice",
			render: (value) => {
				return (
					<Statistic
						title=""
						value={value}
						prefix={"$"}
						precision={3}
						valueStyle={{ fontSize: 14 }}
					/>
				);
			},
		},
		{
			title: "Unit Of Measurement",
			dataIndex: "unitOfMeasurement",
			key: "unitOfMeasurement",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Unit Price Conversion",
			dataIndex: "unitPriceConversion",
			key: "unitPriceConversion",
			render: (value) => {
				return (
					<Statistic
						title=""
						value={value}
						prefix={"$"}
						precision={4}
						valueStyle={{ fontSize: 14 }}
					/>
				);
			},
		},
		{
			title: "Unit Of Conversion",
			dataIndex: "unitOfConversion",
			key: "unitOfConversion",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Production Available",
			dataIndex: "productionAvailable",
			key: "productionAvailable",
			render: (value) => {
				return <Checkbox checked={value} />;
			},
		},
		{
			title: "Production Order Number",
			dataIndex: "productionOrderNumber",
			key: "productionOrderNumber",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Inventory Available",
			dataIndex: "inventoryAvailable",
			key: "inventoryAvailable",
			render: (value) => {
				return <Checkbox checked={value} />;
			},
		},
		{
			title: "Inventory Unit Of Measurement",
			dataIndex: "inventoryUnitOfMeasurement",
			key: "inventoryUnitOfMeasurement",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Logistics Available",
			dataIndex: "logisticsAvailable",
			key: "logisticsAvailable",
			render: (value) => {
				return <Checkbox checked={value} />;
			},
		},
		{
			title: "Franchise",
			dataIndex: "franchise",
			key: "franchise",
			render: (value) => {
				return (
					<Tag color={value === "57Concrete" ? "green" : "cyan"}>{value}</Tag>
				);
			},
		},
		{
			title: "Plant",
			dataIndex: "plants",
			key: "plants",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
	];

	useEffect(() => {
		getCogList();
	}, [franchise]);

	const getCogList = async () => {
		setLoading(true);

		const url = api + `/cog/list/${franchise}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			data.map((item, index) => {
				list.push({
					key: index,
					id: item._id,
					material: item.material,
					unitPrice: item.unitPrice,
					unitOfMeasurement: item.unitOfMeasurement,
					unitPriceConversion: item.unitPriceConversion,
					unitOfConversion: item.unitOfConversion || "N/A",
					productionAvailable: item.productionAvailable || false,
					productionOrderNumber: item.productionOrderNumber || "N/A",
					inventoryAvailable: item.inventoryAvailable || false,
					inventoryUnitOfMeasurement: item.inventoryUnitOfMeasurement || "N/A",
					logisticsAvailable: item.logisticsAvailable || false,
					franchise: item.franchise || "57Concrete",
					code: item.code || "N/A",
					plants:
						item.plants && item.plants.length > 0
							? item.plants.map((p) => p.name).join(", ")
							: "N/A",
				});
			});
			setCogList(list);
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	// Cogs filtered by the search term (material, code or plant)
	const filteredCogList = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return cogList;

		return cogList.filter((item) =>
			[item.material, item.code, item.plants].some((value) =>
				(value ?? "").toString().toLowerCase().includes(term),
			),
		);
	}, [cogList, search]);

	const addNewCog = () => {
		history.push("/cog/new");
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const goBack = () => {
		history.goBack();
	};

	// Rendered inline (not as an inner component) so the search input
	// keeps the focus while typing
	return (
		<Layout title="Cog" description="Cog List">
			<Flex vertical gap={"large"}>
				{loading ? (
					<CustomSpinner />
				) : (
					<Flex gap={"middle"} vertical>
						<Row gutter={16}>
							<Col>
								<Button
									type="primary"
									size="large"
									icon={<RollbackOutlined />}
									onClick={goBack}
								/>
							</Col>
							<Col>
								<Button
									onClick={addNewCog}
									size="large"
									type="primary"
									style={{
										width: 150,
									}}
									icon={<PlusCircleFilled />}
								>
									New Cog
								</Button>
							</Col>
						</Row>

						<Flex gap={"middle"} wrap>
							<Select
								showSearch
								placeholder={`Select franchise`}
								optionFilterProp="children"
								size="large"
								onChange={(value) => setFranchise(value)}
								filterOption={filterOption}
								options={franchises57Options}
								disabled={loading}
								defaultValue={"57Concrete"}
								value={franchise}
								style={{
									width: 200,
								}}
								allowClear={false}
							/>
							<Input.Search
								placeholder="Search by material, code or plant"
								size="large"
								allowClear
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								style={{
									width: 400,
								}}
							/>
						</Flex>
						<CustomTable columns={columns} data={filteredCogList} />
					</Flex>
				)}
			</Flex>
		</Layout>
	);
};

export default CogList;
